import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Palette } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useThemeContext } from './theme-context';
import { usePages } from '@/hooks/useApi';
import { themes } from '@/lib/themes';

const STATIC_LINKS: { path: string; label: string }[] = [{ path: '/blog', label: 'Blog' }];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const themeMenuDesktopRef = useRef<HTMLDivElement>(null);
  const themeMenuMobileRef = useRef<HTMLDivElement>(null);
  const { themeName, setThemeName } = useThemeContext();
  const { data: pages = [] } = usePages();

  // Combine static links with dynamic pages
  const navLinks = [
    ...STATIC_LINKS,
    ...pages.map(page => ({ path: `/${page.slug}`, label: page.title }))
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const clickedInsideDesktop = themeMenuDesktopRef.current?.contains(target);
      const clickedInsideMobile = themeMenuMobileRef.current?.contains(target);

      if (!clickedInsideDesktop && !clickedInsideMobile) {
        setThemeMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-primary dark:bg-slate-900/70 backdrop-blur-md border-b border-transparent dark:border-slate-700/50 transition-colors duration-300">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <motion.div
              className="w-10 h-10 rounded-full bg-white text-primary flex items-center justify-center font-bold text-lg shadow-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              JC
            </motion.div>
            <div className="min-w-0">
              <span className="text-xs sm:text-sm font-medium text-white/80 dark:text-slate-300 transition-colors block">
                Javier Callico
              </span>
              <span className="block text-base sm:text-lg font-bold leading-tight tracking-tight text-white dark:text-slate-100">
                Software Engineer
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-white dark:text-white font-bold'
                      : 'text-white/80 dark:text-slate-300 hover:text-white dark:hover:text-slate-100'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            
            {/* Theme Selector */}
            <div className="relative" ref={themeMenuDesktopRef}>
              <button
                onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 dark:bg-slate-800 hover:bg-white/20 dark:hover:bg-slate-700 transition-colors text-sm font-medium text-white dark:text-slate-200 border border-white/10 dark:border-transparent"
                aria-label="Change theme"
              >
                <Palette className="w-4 h-4" />
                <span className="hidden lg:inline">{themeName}</span>
              </button>

              {themeMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 py-2 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 max-h-96 overflow-y-auto z-50">
                  {Object.keys(themes).map((optionName) => (
                    <button
                      key={optionName}
                      onClick={() => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setThemeName(optionName as any);
                        setThemeMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 ${
                        themeName === optionName ? 'text-primary font-bold bg-slate-50 dark:bg-slate-800/50' : 'text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: themes[optionName as keyof typeof themes].light.primaryColor }}></div>
                      {optionName}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <a
              href="https://github.com/JCallico/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
              aria-label="GitHub"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </a>
            <a
              href="https://x.com/jcallico"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
              aria-label="X"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/in/jcallico/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
              aria-label="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white hover:text-white/80 dark:text-slate-300 dark:hover:text-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4 pb-4 border-t border-slate-200 dark:border-slate-700/50 pt-4"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-white font-bold'
                        : 'text-white/80 hover:text-white'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}

              {/* Mobile Theme Selector */}
              <div className="relative" ref={themeMenuMobileRef}>
                <button
                  onClick={() => setThemeMenuOpen(!themeMenuOpen)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium text-white border border-white/10"
                  aria-label="Change theme"
                >
                  <span className="inline-flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    <span>Theme: {themeName}</span>
                  </span>
                  <span className="text-white/70">▾</span>
                </button>

                {themeMenuOpen && (
                  <div className="absolute left-0 right-0 mt-2 py-2 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 max-h-96 overflow-y-auto z-50">
                    {Object.keys(themes).map((optionName) => (
                      <button
                        key={optionName}
                        onClick={() => {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          setThemeName(optionName as any);
                          setThemeMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 ${
                          themeName === optionName ? 'text-primary font-bold bg-slate-50 dark:bg-slate-800/50' : 'text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: themes[optionName as keyof typeof themes].light.primaryColor }}></div>
                        {optionName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
}
